/*    
<summary>
  This functional component "AddUpdateEmailSetting" defines the props like initial values and functions for the child form component.
  Developer:Aashish Singh, Created Date:13-August-2025
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useStore } from "../../../contexts/store-provider";
import { formatMessage } from "../../../translations/format-message";
import AddUpdateEmailSettingForm from "../forms/add-update-email-setting-form";
import emailTypeEnum from "../../../constants/enums/email-type-enum";
import {
  getHeadquarterIdFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../helpers/localstorage-helper";
import userTypeEnum from "../../../constants/enums/user-type-enum";

const AddUpdateEmailSetting = () => {
  const { emailManagementStore } = useStore();
  const {
    GetHQEmailSettingsService,
    GetStoreEmailSettingsService,
    AddHQEmailSettingService,
    UpdateHQEmailSettingService,
    UpdateStoreEmailSettingService,
    inProgress,
    resetAddUpdateEmailSettingState,
    resetGetHQEmailSettingsDetail,
    resetGetStoreEmailSettingsDetail,
    getHqEmailSettingDetails,
    getStoreEmailSettingDetails,
    addUpdateEmailSettingState,
    emailType,
    AddStoreEmailSettingService,
    disableAction
  } = emailManagementStore;
  const userType = getUserTypeFromLocalStorage();
  const headquarterId = getHeadquarterIdFromLocalStorage();
  const storeId = getStoreIdFromLocalStorage();

  const getData = () => {
    if (userType === userTypeEnum[userTypeEnum.HeadQuarter])
      GetHQEmailSettingsService(headquarterId, emailType);
    else GetStoreEmailSettingsService(headquarterId, emailType, storeId);
  };

  useEffect(() => {
    getData();
  }, [emailType]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateEmailSettingState.success) {
      toast.success(formatMessage("added_success"));
      getData();
      resetAddUpdateEmailSettingState();
    }
  }, [addUpdateEmailSettingState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateEmailSettingState.error) {
      toast.error(formatMessage(addUpdateEmailSettingState.error));
      resetAddUpdateEmailSettingState();
      resetGetHQEmailSettingsDetail();
      resetGetStoreEmailSettingsDetail();
    }
  }, [addUpdateEmailSettingState.error]);

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    let requestBody = {
      EmailBeforeHours:
        emailType === emailTypeEnum.ReservationReminder
          ? values.EmailBeforeHours
          : null,
      EmailAfterHours:
        emailType === emailTypeEnum.VisitingThanks
          ? values.EmailAfterHours
          : null,
      EmailType: emailType,
      EmailBody:
        emailType !== emailTypeEnum.HappyBirthday ? values.EmailBody : null,
      EmailHeader:
        emailType === emailTypeEnum.HappyBirthday ? values.EmailHeader : null,
      EmailFooter:
        emailType === emailTypeEnum.HappyBirthday ? values.EmailFooter : null,
      HeadquarterId: headquarterId,
    };
    if(emailType === emailTypeEnum.HappyBirthday)
        requestBody.EmailBeforeHours = values.BeforeDays*24 + parseInt(values.BeforeHours)
    if (userType === userTypeEnum[userTypeEnum.HeadQuarter]) {
      if (getHqEmailSettingDetails?.Id > 0) {
        UpdateHQEmailSettingService({
          ...requestBody,
        });
      } else
        AddHQEmailSettingService({
          ...requestBody,
        });
    } else {
      if(getStoreEmailSettingDetails?.StoreId > 0){
        UpdateStoreEmailSettingService({
          StoreId: storeId,
          EmailBody: values.StoreEmailBody,
          EmailType: emailType,
          IsSend: values.IsSend,
          HeadquarterId: headquarterId,
        });
      }
      else{
        AddStoreEmailSettingService({
          StoreId: storeId,
          EmailBody: values.StoreEmailBody,
          EmailType: emailType,
          IsSend: values.IsSend,
          HeadquarterId: headquarterId,
        });
      }
    }
  };

  return (
    <React.Fragment>
      <AddUpdateEmailSettingForm
        emailType={emailType}
        userType={userType}
        initialValues={
          userType === userTypeEnum[userTypeEnum.HeadQuarter]
            ? getHqEmailSettingDetails
            : getStoreEmailSettingDetails
        }
        submitHandler={onSubmitForm}
        disableAction={disableAction}
        disabled={userType !== userTypeEnum[userTypeEnum.HeadQuarter]}
        isLoading={addUpdateEmailSettingState.inProgress || inProgress}
        addUpdateEmailSettingState={addUpdateEmailSettingState}
      />
    </React.Fragment>
  );
};

export default observer(AddUpdateEmailSetting);
